import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { reviewAPI, emailAPI, feedbackAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Mail, Clock, CheckCircle, Play, Loader2 } from 'lucide-react';
import { InfoTooltip } from '../components/InfoTooltip';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showProgress, setShowProgress] = useState(false);
  const [jobId, setJobId] = useState<string | null>(() => localStorage.getItem('processing_job_id'));
  const [progress, setProgress] = useState({ processed: 0, total: 0, status: 'running', current_email: '' });
  const [maxEmails, setMaxEmails] = useState(50);
  const [monitorStatus, setMonitorStatus] = useState({ 
    running: false, 
    poll_interval: 60,
    amazon_enabled: true,
    ebay_enabled: true
  });

  // Fetch monitor status
  const { data: monitorData } = useQuery({
    queryKey: ['monitor-status'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/emails/monitor/status`);
      setMonitorStatus(response.data);
      return response.data;
    },
    refetchInterval: () => {
      // Poll every 5 seconds if monitoring is running, otherwise every 30 seconds
      return monitorStatus.running ? 5000 : 30000;
    },
  });

  // Check for existing job on mount
  useEffect(() => {
    const savedJobId = localStorage.getItem('processing_job_id');
    if (savedJobId) {
      setJobId(savedJobId);
      // Fetch current status immediately
      axios.get(`${API_BASE_URL}/emails/processing/${savedJobId}`)
        .then(response => {
          setProgress(response.data);
          setShowProgress(true);
        })
        .catch(error => {
          console.error('Error fetching initial status:', error);
          localStorage.removeItem('processing_job_id');
          setShowProgress(false);
        });
    }
  }, []);

  const { data: stats } = useQuery({
    queryKey: ['review-stats'],
    queryFn: async () => {
      const response = await reviewAPI.getStats();
      return response.data;
    },
    refetchInterval: monitorStatus.running ? 10000 : false, // Refresh every 10 seconds when monitoring
  });

  // Fetch feedback stats
  const { data: feedbackStats } = useQuery({
    queryKey: ['feedback-stats'],
    queryFn: async () => {
      const response = await feedbackAPI.getStats();
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Poll for progress updates
  useEffect(() => {
    if (!jobId || (progress.status !== 'running' && progress.status !== '')) return;

    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/emails/processing/${jobId}`);
        setProgress(response.data);
        
        // Refresh stats while processing to show real-time updates
        if (response.data.status === 'running') {
          queryClient.invalidateQueries({ queryKey: ['review-stats'] });
        }
        
        if (response.data.status === 'completed') {
          toast.success(`Processed ${response.data.processed} emails successfully`);
          queryClient.invalidateQueries({ queryKey: ['review-stats'] });
          localStorage.removeItem('processing_job_id');
          setTimeout(() => {
            setShowProgress(false);
            setJobId(null);
          }, 3000); // Hide after 3 seconds
        } else if (response.data.status === 'failed') {
          toast.error('Failed to process emails');
          localStorage.removeItem('processing_job_id');
        } else if (response.data.status === 'cancelled') {
          localStorage.removeItem('processing_job_id');
          setTimeout(() => {
            setShowProgress(false);
            setJobId(null);
          }, 2000);
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
      }
    }, 1000); // Poll every second

    return () => clearInterval(interval);
  }, [jobId, progress.status, queryClient]);

  const handleProcessEmails = async () => {
    try {
      const response = await emailAPI.process({ max_emails: maxEmails });
      const newJobId = response.data.job_id;
      setJobId(newJobId);
      localStorage.setItem('processing_job_id', newJobId);
      setProgress({ processed: 0, total: response.data.max_emails, status: 'running', current_email: '' });
      setShowProgress(true);
    } catch (error) {
      toast.error('Failed to start processing');
    }
  };

  const handleToggleMonitor = async () => {
    try {
      if (monitorStatus.running) {
        await axios.post(`${API_BASE_URL}/emails/monitor/stop`);
        toast.success('Auto-monitoring stopped');
      } else {
        await axios.post(`${API_BASE_URL}/emails/monitor/start`);
        toast.success('Auto-monitoring started');
      }
      queryClient.invalidateQueries({ queryKey: ['monitor-status'] });
    } catch (error) {
      toast.error('Failed to toggle monitor');
    }
  };

  const handleToggleAmazon = async () => {
    try {
      await axios.post(`${API_BASE_URL}/emails/monitor/toggle/amazon`);
      queryClient.invalidateQueries({ queryKey: ['monitor-status'] });
      toast.success(monitorStatus.amazon_enabled ? 'Amazon monitoring disabled' : 'Amazon monitoring enabled');
    } catch (error) {
      toast.error('Failed to toggle Amazon monitoring');
    }
  };

  const handleToggleEbay = async () => {
    try {
      await axios.post(`${API_BASE_URL}/emails/monitor/toggle/ebay`);
      queryClient.invalidateQueries({ queryKey: ['monitor-status'] });
      toast.success(monitorStatus.ebay_enabled ? 'eBay monitoring disabled' : 'eBay monitoring enabled');
    } catch (error) {
      toast.error('Failed to toggle eBay monitoring');
    }
  };

  const handleCancelProcessing = async () => {
    if (!jobId) return;
    
    try {
      await axios.post(`${API_BASE_URL}/emails/processing/${jobId}/cancel`);
      toast.success('Processing cancelled');
      localStorage.removeItem('processing_job_id');
      setShowProgress(false);
      setJobId(null);
    } catch (error) {
      toast.error('Failed to cancel processing');
    }
  };

  const statCards = [
    {
      title: 'Pending Review',
      value: stats?.pending_review || 0,
      description: 'Emails waiting for review',
      tooltip: 'AI-generated responses awaiting human review and approval before sending to customers',
      onClick: () => navigate('/review'),
    },
    {
      title: 'Approved',
      value: stats?.approved || 0,
      description: 'Ready to send',
      tooltip: 'Responses that have been reviewed and approved, ready to be sent to customers',
      onClick: () => navigate('/approved'),
    },
    {
      title: 'Sent',
      value: stats?.sent || 0,
      description: 'Successfully sent emails',
      tooltip: 'Responses that have been successfully sent to customers via email',
      onClick: () => navigate('/emails?status=sent'),
    },
    {
      title: 'Rejected',
      value: stats?.rejected || 0,
      description: 'Rejected emails',
      tooltip: 'Responses that were reviewed and rejected, will not be sent to customers',
      onClick: () => navigate('/emails?status=rejected'),
    },
    {
      title: 'Total Processed',
      value: stats?.total || 0,
      description: 'All time emails processed',
      tooltip: 'Total number of customer emails processed by the system since inception',
      onClick: () => navigate('/emails'),
    },
  ];

  // Feedback stats cards
  const feedbackCards = [
    {
      title: 'Response Quality',
      value: feedbackStats?.total_feedback || 0,
      description: 'Total responses rated',
      color: 'blue',
    },
    {
      title: 'Upvotes',
      value: feedbackStats?.upvotes || 0,
      description: 'Positive ratings',
      color: 'green',
    },
    {
      title: 'Downvotes', 
      value: feedbackStats?.downvotes || 0,
      description: 'Negative ratings',
      color: 'red',
    },
    {
      title: 'Quality Score',
      value: feedbackStats?.upvote_percentage ? `${feedbackStats.upvote_percentage}%` : '0%',
      description: 'Upvote percentage',
      color: 'purple',
    },
  ];

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Overview of your email automation system
        </p>
      </div>

      {/* Email Processing Controls */}
      <div className="grid gap-4 md:grid-cols-1">
        {/* Auto-Monitor */}
        <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold mb-1 flex items-center gap-2">
                  Auto-Monitor
                  <InfoTooltip content="Automatically monitor Gmail and eBay for new messages and process them at regular intervals" />
                </h3>
                <p className="text-sm text-muted-foreground">
                  Check for new messages every {monitorStatus.poll_interval} seconds
                </p>
              </div>
              <Button
                onClick={handleToggleMonitor}
                variant="outline"
                className={monitorStatus.running ? 'border-green-200 text-green-700 hover:bg-green-50' : ''}
              >
                {monitorStatus.running ? 'Stop All' : 'Start All'}
              </Button>
            </div>
            
            {/* Platform Toggles */}
            <div className="grid grid-cols-2 gap-3 pt-3 border-t">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Amazon</span>
                  <span className="px-2 py-0.5 text-xs rounded-md bg-orange-50 text-orange-700 border border-orange-200 font-medium">
                    AMAZON
                  </span>
                </div>
                <Button
                  onClick={handleToggleAmazon}
                  size="sm"
                  variant="outline"
                  className={monitorStatus.amazon_enabled ? 'border-green-200 text-green-700' : 'border-gray-300'}
                >
                  {monitorStatus.amazon_enabled ? 'ON' : 'OFF'}
                </Button>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">eBay</span>
                  <span className="px-2 py-0.5 text-xs rounded-md bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                    EBAY
                  </span>
                </div>
                <Button
                  onClick={handleToggleEbay}
                  size="sm"
                  variant="outline"
                  className={monitorStatus.ebay_enabled ? 'border-green-200 text-green-700' : 'border-gray-300'}
                >
                  {monitorStatus.ebay_enabled ? 'ON' : 'OFF'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>

      {/* Stats - Side by Side Equal Width */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Email Stats */}
        <div className="space-y-1.5">
          <h2 className="text-base font-semibold mb-2">Email Statistics</h2>
          {statCards.map((stat) => {
            return (
              <div
                key={stat.title}
                className="flex items-center justify-between p-2.5 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors group"
                onClick={stat.onClick}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{stat.title}</span>
                  <InfoTooltip content={stat.tooltip} />
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xl font-bold text-gray-900">{stat.value}</span>
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>

        {/* Response Quality Stats */}
        <div className="space-y-1.5">
          <h2 className="text-base font-semibold mb-2">Response Quality Analytics</h2>
          {feedbackCards.map((stat) => {
            const colorClasses = {
              blue: 'border-blue-200 bg-blue-50 hover:bg-blue-100',
              green: 'border-green-200 bg-green-50 hover:bg-green-100',
              red: 'border-red-200 bg-red-50 hover:bg-red-100',
              purple: 'border-purple-200 bg-purple-50 hover:bg-purple-100',
            };
            
            return (
              <div
                key={stat.title}
                className={`flex items-center justify-between py-3.5 px-4 border rounded-lg transition-colors ${colorClasses[stat.color as keyof typeof colorClasses]}`}
              >
                <span className="text-sm font-medium text-gray-700">{stat.title}</span>
                <span className="text-xl font-bold text-gray-900">{stat.value}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Quick Actions</CardTitle>
          <CardDescription className="text-sm">
            Common tasks and navigation
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <Button
            onClick={() => navigate('/review?status=pending_review')}
            className="h-auto py-3 justify-start"
            variant="outline"
          >
            <div className="text-left flex-1">
              <div className="font-semibold flex items-center gap-2 text-sm">
                Review Queue
                <InfoTooltip content="Review AI-generated responses, approve or reject them, and provide feedback before sending to customers" />
              </div>
              <div className="text-sm text-muted-foreground font-normal">
                Review and approve pending emails
              </div>
            </div>
          </Button>
          <Button
            onClick={() => navigate('/approved')}
            className="h-auto py-4 justify-start"
            variant="outline"
          >
            <div className="text-left flex-1">
              <div className="font-semibold flex items-center gap-2">
                Send Emails
                <InfoTooltip content="Send approved email responses to customers via Gmail. Bulk send multiple emails at once" />
              </div>
              <div className="text-sm text-muted-foreground font-normal">
                Send approved emails to customers
              </div>
            </div>
          </Button>
          <Button
            onClick={() => navigate('/settings')}
            className="h-auto py-4 justify-start"
            variant="outline"
          >
            <div className="text-left flex-1">
              <div className="font-semibold flex items-center gap-2">
                Settings
                <InfoTooltip content="Configure AI system prompts, category-specific prompts, API keys, and email templates" />
              </div>
              <div className="text-sm text-muted-foreground font-normal">
                Configure prompts and API settings
              </div>
            </div>
          </Button>
          <Button
            onClick={() => navigate('/emails')}
            className="h-auto py-4 justify-start"
            variant="outline"
          >
            <div className="text-left flex-1">
              <div className="font-semibold flex items-center gap-2">
                All Emails
                <InfoTooltip content="View complete email history with advanced filters by category, status, timeframe, and order ID" />
              </div>
              <div className="text-sm text-muted-foreground font-normal">
                View complete email history
              </div>
            </div>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
